import React from 'react';
import { motion } from 'framer-motion';
import { Star, Phone } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';

export const HeroSection = ({ onBookClick }) => {
  const { t, isRTL } = useLanguage();

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="hero-section relative min-h-screen flex items-center justify-center"
      style={{ 
        background: 'linear-gradient(135deg, #f8f6f0 0%, #e8f4f8 50%, #f0e8e0 100%)'
      }}
    >
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Subtle wave pattern at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#d4e5eb]/30 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 md:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto"
        >
          {/* Logo Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-8"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_rose-gold-salon-2/artifacts/0gqiaw1n_sirensa%201.jpeg"
              alt="Sirena Beauty Center"
              className="w-72 md:w-96 lg:w-[450px] h-auto mx-auto drop-shadow-lg"
            />
          </motion.div>

          {/* Rating Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-full px-5 py-2.5 mb-6 shadow-md"
          >
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-4 h-4 ${i < 4 ? 'text-yellow-500 fill-yellow-500' : 'text-yellow-500/50 fill-yellow-500/50'}`}
                />
              ))}
            </div>
            <span className="text-[#4A4A4A] text-sm font-medium">
              4.7 {t.hero.rating} (12 {t.hero.reviews})
            </span>
          </motion.div>

          {/* Tagline */}
          <motion.h1
            data-testid="hero-tagline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="heading text-3xl sm:text-4xl md:text-5xl font-bold text-[#4A4A4A] mb-4 leading-tight"
          >
            {t.hero.tagline}
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="text-base md:text-lg text-[#7D7D7D] mb-8 max-w-2xl mx-auto"
          >
            {t.hero.subtitle}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Button
              data-testid="hero-book-btn"
              onClick={onBookClick}
              className="bg-[#C9A050] hover:bg-[#B8903F] text-white rounded-full text-base px-8 py-4 shadow-lg hover:shadow-xl transition-all"
            >
              {t.hero.bookAppointment}
            </Button>
            <Button
              data-testid="hero-call-btn"
              variant="outline"
              asChild
              className="border-2 border-[#C9A050] text-[#C9A050] hover:bg-[#C9A050]/10 rounded-full px-8 py-4"
            >
              <a href="tel:+966537637775" className="flex items-center gap-2">
                <Phone className="w-5 h-5" />
                {t.hero.callNow}
              </a>
            </Button>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-6 h-10 border-2 border-[#C9A050]/50 rounded-full flex items-start justify-center p-2"
          >
            <div className="w-1.5 h-3 bg-[#C9A050]/70 rounded-full" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
